import {
  Button,
  Caption1,
  Caption1Stronger,
  mergeClasses,
  Tooltip,
} from '@fluentui/react-components'
import { TargetSparkleRegular } from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import { REVIEW_MODE_ENABLED } from '../../constants/chat'
import { useChatContext, useDocumentComments, useImatch, useMouseSelection } from '../../hooks'
import { useChatSession } from '../../hooks/useChatSession'

import { CommentsContextBadge } from './CommentsContextBadge'
import { ContextListFrame } from './ContextListFrame'
import { DocumentContextBadge } from './DocumentContextBadge'
import { ItemsContextPill } from './ItemsContextPill'
import { MouseSelectionBadge } from './MouseSelectionBadge'
import { StatusCard } from '../StatusCard/StatusCard'
import { useStyles } from './ContextBar.styles'

interface ContextBarProps {
  isExpanded: boolean
  onExpandedChange: (next: boolean) => void
  /** The list's resolved height for this render — already the final answer, not a cap to fit
   *  content within. ChatInput computes this (via allocateComposerHeights); ContextBar just
   *  passes it through to ContextListFrame. */
  allocatedHeight?: number
  animateListResize?: boolean
  onListContentHeightChange?: (height: number) => void
  headerRowRef?: React.RefObject<HTMLDivElement | null>
}

/**
 * Chat context header row + expandable context card list.
 * Tricky header UX decisions are locked in CONTEXT_ROW.md and the unit tests.
 */
const ContextBar = ({
  isExpanded,
  onExpandedChange,
  allocatedHeight = 140,
  animateListResize = true,
  onListContentHeightChange,
  headerRowRef,
}: ContextBarProps) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { contextItems, activeContextCount } = useChatContext()
  const { mutate: runImatch, isPending: isImatchPending } = useImatch()
  const { hasMouseSelection } = useMouseSelection()
  const { draftMessage, includeComments, setIncludeComments } = useChatSession()
  // Only tracked when the badge can actually render - no point paying for Word.run()
  // round-trips behind a feature flag nobody sees the result of.
  const { hasComments } = useDocumentComments(REVIEW_MODE_ENABLED)
  const isDisabledDueToContent = !hasMouseSelection && !draftMessage

  // Imatch temporarily takes over the whole context bar (see the loading-text swap
  // above), so the context card list collapses too, regardless of the user's manual
  // expand/collapse preference. It reappears with that same preference once imatch settles.
  const isListVisible = isExpanded && !isImatchPending

  const listAriaLabel = t('contextBar.itemsCount', { count: activeContextCount })

  return (
    <div className={classes.contextBar}>
      <div ref={headerRowRef} className={classes.headerRow}>
        <div className={classes.headerLeft}>
          <ItemsContextPill
            activeContextCount={activeContextCount}
            isExpanded={isExpanded}
            onExpandedChange={onExpandedChange}
            hideExtended={isImatchPending}
          />
          {isImatchPending ? (
            // While imatch runs, badges are replaced by loading text - only the
            // paperclip attach control above stays visible.
            <Caption1 className={mergeClasses(classes.contextCountText, classes.loadingText)}>
              {t('chatInput.contextAssistLoading')}
            </Caption1>
          ) : (
            <>
              <MouseSelectionBadge hasMouseSelection={hasMouseSelection} />
              <DocumentContextBadge />
              {REVIEW_MODE_ENABLED && (
                <CommentsContextBadge
                  isActive={includeComments}
                  onChange={setIncludeComments}
                  disabled={!hasComments}
                />
              )}
            </>
          )}
        </div>
        <Tooltip
          content={
            <div className={classes.tooltipContent}>
              <Caption1Stronger>{t('chatInput.contextAssist')}</Caption1Stronger>
              <Caption1 as="p">{t('chatInput.contextAssistDescription')}</Caption1>
              {isDisabledDueToContent && (
                <StatusCard status="info">{t('chatInput.contextAssistDisabledHint')}</StatusCard>
              )}
            </div>
          }
          relationship="description"
          appearance="inverted"
          showDelay={1000}>
          <Button
            className={classes.rightActionButton}
            appearance="subtle"
            size="medium"
            shape="circular"
            icon={<TargetSparkleRegular />}
            aria-label={t('chatInput.contextAssist')}
            onClick={() => {
              onExpandedChange(false)
              // Per-call onSuccess runs after the hook's own onSuccess (notifications, context update).
              // Expand the list only when iMatch found results.
              runImatch(undefined, {
                onSuccess: ({ selections }) => {
                  if (selections.length > 0) onExpandedChange(true)
                },
              })
            }}
            disabled={isImatchPending}
            disabledFocusable={isDisabledDueToContent && !isImatchPending}
          />
        </Tooltip>
      </div>

      <ContextListFrame
        isVisible={isListVisible}
        allocatedHeight={allocatedHeight}
        animateResize={animateListResize}
        ariaLabel={listAriaLabel}
        items={contextItems}
        onContentHeightChange={onListContentHeightChange}
      />
    </div>
  )
}

export { ContextBar }
